
import { Component, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteGroupRepositoryInterface, { WebsiteGroup } from '../../../services/WebsiteGroup/interfaces';
import { DatabaseRepositoryInterface } from '@/services/Database/interfaces';
import VComponent from '@/components/base/VComponent.vue';
import MigrateWebsiteGroupDialog from '@/components/dialogs/website/MigrateWebsiteGroupDialog.vue';
import WebsiteRepositoryInterface, { Website, WebsiteStatus } from '@/services/Website/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { UserRepoInterface, UserResponseInterface } from '@/services/Users/Interfaces';
import { PlanInterface, PlanRepositoryInterface } from '@/services/Plan/interfaces';
import ChangePlanDialog from '@/components/dialogs/website/ChangePlanDialog.vue';
import { SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import ChangeGroupOwnerDialog from '@/components/dialogs/website/ChangeGroupOwnerDialog.vue';
import SyncGroupDatabasesDialog from '@/components/dialogs/website/SyncGroupDatabasesDialog.vue';
import TerminateGroupDialog from '@/components/dialogs/website/TerminateGroupDialog.vue';
import ToggleGroupDialog from '@/components/dialogs/website/ToggleGroupDialog.vue';
import ChangeHBIdDialog from '@/components/dialogs/website/ChangeGroupIdDialog.vue';
import { BackupRepositoryInterface } from "@/services/Backup/interfaces";
import {
  QuotaAndGroupUsage,
  UsageRepositoryInterface,
  UserCpuResult,
  WebsiteGroupBandwidthUsage, WebsiteGroupSnapshot,
  WebsiteGroupUsageUpdateError,
} from '@/services/Usage/interfaces';

@Component({
  mixins: [Themeable],
  inject: [
    'websiteGroupRepository',
    'websiteRepository',
    'serverRepository',
    'userRepository',
    'planRepository',
    'systemUserRepository',
    'databaseRepository',
    'backupRepository',
    'usageRepository',
  ],
  components: {
    VComponent,
    MigrateWebsiteGroupDialog,
    ChangePlanDialog,
    ChangeGroupOwnerDialog,
    ChangeHBIdDialog,
    SyncGroupDatabasesDialog,
    TerminateGroupDialog,
    ToggleGroupDialog,
  },
  sockets: {
    websiteGroupUsageUpdated(
      msg: { payload: WebsiteGroupSnapshot },
    ): void {
      this.$emit('websiteGroupUsageReceived', msg.payload);
    },

    websiteGroupUsageUpdateError(
      msg: { payload: WebsiteGroupUsageUpdateError },
    ): void {
      this.$emit('websiteGroupUsageErrorReceived', msg.payload);
    },
  },
})
export default class ViewWebsite extends Vue {
  public loading = true;

  private readonly backupRepository!: BackupRepositoryInterface;

  private readonly websiteGroupRepository!: WebsiteGroupRepositoryInterface;

  private readonly serverRepository!: ServerRepositoryInterface;

  private readonly userRepository!: UserRepoInterface;

  private readonly planRepository!: PlanRepositoryInterface;

  private readonly systemUserRepository!: SystemUserRepositoryInterface;

  private readonly websiteRepository!: WebsiteRepositoryInterface;

  private readonly databaseRepository!: DatabaseRepositoryInterface;

  private readonly usageRepository!: UsageRepositoryInterface;

  public websitesHeaders: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Domain', value: 'domain' },
    { text: 'Server', value: 'originServer' },
    { text: 'Status', value: 'status' },
  ];

  public stateStatusReverseMap: { [key: string]: string } = {
    ACTIVE: 'DISABLED',
    DISABLED: 'ACTIVE',
  };

  public diskUsage: QuotaAndGroupUsage | null = null;

  public bandwidthUsage: WebsiteGroupBandwidthUsage | null = null;

  public cpu24h: UserCpuResult | null = null;
  public cpu7d: UserCpuResult | null = null;
  public cpu30d: UserCpuResult | null = null;

  public usageLoading = true;

  public group: WebsiteGroup = {} as WebsiteGroup;

  public mainWebsite: Website | undefined;

  public migrateWebsiteGroupDialog: string = '';

  public changePlanDialog: string = '';

  public serversMap: { [key: string]: string } = {};

  public user: UserResponseInterface = {} as UserResponseInterface;

  public systemUserId: number | null = null;

  public plan: PlanInterface = {} as PlanInterface;

  public changeOwnerDialog: string = '';

  public syncDatabasesSizeDialog: string = '';

  public terminateGroupDialog: string = '';

  public activateGroupDialog: string = '';

  public deactiveGroupDialog: string = '';

  public changeHBIdDialog: string = '';

  public syncingQuotas = false;
  public isRefreshDisabled = false;
  private refreshEnableTimer: number | null = null;

  private scheduleRefreshEnable(): void {
    if (this.refreshEnableTimer !== null) {
      clearTimeout(this.refreshEnableTimer);
      this.refreshEnableTimer = null;
    }

    const snapshot = this.diskUsage?.websiteGroupSnapshot;

    if (!snapshot?.date || !snapshot?.interval) {
      this.isRefreshDisabled = false;
      return;
    }

    const remaining =
      (snapshot.date + snapshot.interval) * 1000 - Date.now();

    this.isRefreshDisabled = remaining > 0;

    if (remaining > 0) {
      this.refreshEnableTimer = window.setTimeout(() => {
        this.isRefreshDisabled = false;
        this.refreshEnableTimer = null;
      }, remaining);
    }
  }

  public async syncQuotas(): Promise<void> {
    if (this.syncingQuotas || this.isRefreshDisabled) {
      return;
    }

    this.syncingQuotas = true;

    try {
      await this.usageRepository.syncWebsiteGroupDiskUsageByGroupId(
        this.group.id,
      );

      this.$successDialog.show(
        'Quota synchronization started successfully.',
      );
    } catch (err) {
      this.syncingQuotas = false;

      this.$errorDialog.show(
        err?.response?.data?.error ||
        err?.message ||
        'Failed to start quota synchronization.',
      );
    }
  }

  public handleUsageUpdated(websiteGroupSnapshot: WebsiteGroupSnapshot): void {
    if (!this.diskUsage) {
      return;
    }

    this.diskUsage.websiteGroupSnapshot = websiteGroupSnapshot;
    this.syncingQuotas = false;
    this.scheduleRefreshEnable();
  }

  public handleUsageUpdateError(
    error: WebsiteGroupUsageUpdateError,
  ): void {
    if (error.groupId !== this.group.id) {
      return;
    }

    this.syncingQuotas = false;

    this.$errorDialog.show(
      error.reason || 'Failed to synchronize quota usage.',
    );
  }

  public async created(): Promise<void> {
    this.group = await this.websiteGroupRepository.getGroup(Number(this.$route.params['id']));

    this.mainWebsite = this.group.websites.find((w) => w.id === this.group.mainWebsite);

    const serversIds: string[] = [];
    this.group.websites.forEach((website) => {
      if (serversIds.includes(website.originServer)) {
        return;
      }

      serversIds.push(website.originServer);
    });

    const servers = await this.serverRepository.getServersByIds(serversIds);
    servers.forEach((server) => {
      this.serversMap[server._id] = server.hostname;
    });

    if (this.group.owner.length === 24) {
      this.user = await this.userRepository.getUser(this.group.owner);
    }


    this.plan = await this.planRepository.getOneById(this.group.plan);

    await this.loadUsageOverview();
    this.scheduleRefreshEnable();
    this.$on('websiteGroupUsageReceived', this.handleUsageUpdated);
    this.$on('websiteGroupUsageErrorReceived', this.handleUsageUpdateError);
    this.loading = false;
  }

  public beforeDestroy(): void {
    if (this.refreshEnableTimer !== null) {
      clearTimeout(this.refreshEnableTimer);
    }
  }

  public async loadUsageOverview(): Promise<void> {
    this.usageLoading = true;

    try {
      const from = this.getBeginningOfMonthUnixSeconds();

      await Promise.allSettled([
        this.usageRepository.getDiskUsageByGroup(this.group.id).then(diskUsage => this.diskUsage = diskUsage),
        this.usageRepository.getBandwidthUsageByGroup(this.group.id, from).then(bandwidthUsage => this.bandwidthUsage = bandwidthUsage),
      ]);

      const website = await this.websiteRepository.getWebsiteById(this.group.mainWebsite);
      const systemUserId = website?.details?.unixUserId;

      this.systemUserId = systemUserId;

      if (systemUserId) {
        const [cpu24h, cpu7d, cpu30d] = await Promise.all([
          this.usageRepository.getCpuUsage(String(systemUserId), '24h'),
          this.usageRepository.getCpuUsage(String(systemUserId), '7d'),
          this.usageRepository.getCpuUsage(String(systemUserId), '30d'),
        ]);

        this.cpu24h = cpu24h;
        this.cpu7d = cpu7d;
        this.cpu30d = cpu30d;
      }
    } catch (err) {
      console.error('Failed to load usage overview', err);
    } finally {
      this.usageLoading = false;
    }
  }

  public get bandwidthTotalUsageBytes(): number {
    return this.bandwidthUsage?.totalUsage || 0;
  }

  public get bandwidthQuotaBytes(): number {
    return this.plan.bandwidth * 1024;
  }

  get updateIntervalMinutes(): number {
    return (this.diskUsage?.quotaSnapshot?.interval || 600) / 60;
  }

  get refreshIntervalMinutes(): number | null {
    return (this.diskUsage?.websiteGroupSnapshot?.interval || 1800) / 60;
  }

  public get bandwidthUsagePercent(): number {
    if (!this.bandwidthQuotaBytes) {
      return 0;
    }

    return Math.min((this.bandwidthTotalUsageBytes / this.bandwidthQuotaBytes) * 100, 100);
  }

  public getBeginningOfMonthUnixSeconds(): number {
    const now = new Date();

    return Math.floor(
      new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).getTime() / 1000,
    );
  }

  public goToEditSystemUser(): void {
    if (!this.systemUserId) {
      this.$errorDialog.show('System user was not found for this website group.');
      return;
    }

    this.$router.push({
      name: 'system_user_edit',
      params: { systemUserId: String(this.systemUserId) },
    });
  }

  public showMigrateDialog(): void {
    this.migrateWebsiteGroupDialog = 'migrate-website-group-dialog';
  }

  public async migrateGroup(sourceServer: string, targetServer: string): Promise<void> {
    try {
      await this.websiteGroupRepository.migrateGroup(this.group.id, sourceServer, targetServer);
      this.$successDialog.show('Group Migration Started!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public canMigrate(): boolean {
    return this.group.websites.every((w) => w.status === WebsiteStatus.ACTIVE);
  }

  public showChangePlanDialog(): void {
    this.changePlanDialog = 'change-plan-dialog';
  }

  public async changePlan(newPlan: number): Promise<void> {
    try {
      const website = await this.websiteRepository.getWebsiteById(this.group.mainWebsite);
      await this.systemUserRepository.changePlan(
        website.details.unixUserId,
        newPlan,
      );

      this.$successDialog.show('Change Plan Started!');
    } catch (err) {
      this.$errorDialog.show('Something went wrong while starting plan change!');
    }
  }

  public showChangeOwnerDialog(): void {
    this.changeOwnerDialog = 'change-group-owner-dialog';
  }

  public async changeOwner(newOwner: string): Promise<void> {
    try {
      await this.websiteGroupRepository.changeGroupOwner(this.group.id, newOwner);

      this.$successDialog.show('Group owner changed successfully!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showChangeHBIdDialog(): void {
    this.changeHBIdDialog = 'change-h-b-id-dialog';
  }

  public async changeHBId(newHBId: number): Promise<void> {
    try {
      await this.websiteGroupRepository.changeHBId(this.group.id, newHBId);

      this.$successDialog.show('Group ID changed successfully!');
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }


  public showSyncDatabasesSizeDialog(): void {
    this.syncDatabasesSizeDialog = 'sync-group-databases-dialog';
  }

  public async syncDatabasesSize(): Promise<void> {
    try {
      await this.databaseRepository.syncGroupDatabases(this.group.id);

      this.$successDialog.show(`Group databases size synchronization started!`);
    } catch (err) {
      this.$errorDialog.show(`Failed to begin synchronization of databases size!`);
    }
  }

  public showTerminateGroupDialog(): void {
    this.terminateGroupDialog = 'terminate-group-dialog';
  }

  public async terminateGroup(): Promise<void> {
    try {
      await this.websiteGroupRepository.terminateGroup(this.group.id);

      this.$successDialog.show(`Group termination started!`);
    } catch (err) {
      this.$errorDialog.show(`Failed to begin termination of group!`);
    }
  }

  public showActivateGroupDialog(): void {
    this.activateGroupDialog = 'toggle-group-dialog';
  }

  public showDeactivateGroupDialog(): void {
    this.deactiveGroupDialog = 'toggle-group-dialog';
  }

  public async toggleGroup(state: string): Promise<void> {
    try {
      await this.websiteGroupRepository.toggleGroup(this.group.id, state);

      this.$successDialog.show(`Group toggle started!`);
    } catch (err) {
      this.$errorDialog.show(`Failed to begin toggle of group!`);
    }
  }

  public async backupWebsiteGroup(): Promise<void> {
    try {
      await this.backupRepository.createTerminatedBackup(this.group.id);

      this.$successDialog.show(`Group terminated backup started!`);
    } catch (err) {
      this.$errorDialog.show(`Failed to begin backup of group!`);
    }
  }

  public get totalDiskUsedBytes(): number {
    if (!this.diskUsage) {
      return 0;
    }

    return this.diskUsage.quotaSnapshot.blockUsed;
  }

  public get userOnlyDiskUsedBytes(): number {
    if (!this.diskUsage) {
      return 0;
    }

    return this.diskUsage.quotaSnapshot.userOnlyUsed;
  }

  public get totalDatabasesUsedBytes(): number {
    if (!this.diskUsage || !this.diskUsage.websiteGroupSnapshot) {
      return 0;
    }

    return this.diskUsage.websiteGroupSnapshot.websiteUsage.reduce((sum, website) => {
      return sum + website.databasesUsage.reduce((dbSum, db) => dbSum + (db.size || 0), 0);
    }, 0);
  }

  public get totalMailboxUsedBytes(): number {
    if (!this.diskUsage || !this.diskUsage.websiteGroupSnapshot) {
      return 0;
    }

    return this.diskUsage.websiteGroupSnapshot.websiteUsage.reduce((sum, website) => {
      return sum + website.mailboxUsage.reduce((mailSum, mailbox) => {
        return mailSum + ((mailbox.blockUsed || 0));
      }, 0);
    }, 0);
  }

  public get totalDiskQuotaBytes(): number {
    return this.diskUsage?.quotaSnapshot?.blockSoft || 0;
  }

  public get totalUsedBytes(): number {
    if (!this.diskUsage) {
      return 0;
    }
    return this.diskUsage.quotaSnapshot.blockUsed + this.totalDatabasesUsedBytes;
  }

  public get cpu24hTotal(): number {
    return this.sumCpuPoints(this.cpu24h);
  }

  public get cpu7dTotal(): number {
    return this.sumCpuPoints(this.cpu7d);
  }

  public get cpu30dTotal(): number {
    return this.sumCpuPoints(this.cpu30d);
  }

  public sumCpuPoints(result: UserCpuResult | null): number {
    if (!result?.points?.length) {
      return 0;
    }

    const totalNanoseconds = result.points.reduce((sum, point) => {
      return sum + Number(point.value || 0);
    }, 0);

    return totalNanoseconds / 60_000_000_000;
  }

  public formatBytes(bytes: number): string {
    if (!bytes) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }

    return `${value.toFixed(2)} ${units[unitIndex]}`;
  }

  public formatMinutes(minutes: number): string {
    if (!minutes) {
      return '0 min';
    }

    return `${minutes.toFixed(0)} min`;
  }
}
